import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Check,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Pencil,
  Plus,
  Scan,
  Stethoscope,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { DatePickerField } from '@/shared/ui/components';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSaveDocument } from '@/documents/services/use-save-document';
import { useUploadDocument } from '@/documents/services/use-upload-document';
import { useOrganizationForms } from '@/documents/services/use-organization-forms';
import { useConsentTemplates } from '@/consent/services';
import {
  isDocumentTemplate,
  type ConsentTemplate,
} from '@/consent/types/consent-template-api';
import { DocumentType } from '@/documents/types/document';
import {
  DEFAULT_DOCUMENT_MAX_UPLOAD_MB,
  DOCUMENT_ACCEPT_ATTR,
  TOAST_DOCUMENT_FILE_INVALID,
  TOAST_UPLOAD_LIMIT_MISSING,
  isFileSizeGreater,
  isMimeConsistent,
  isValidExtension,
  oversizeMessage,
} from '@/documents/lib/file-allowlist';
import { useAuthStore } from '@/auth/auth-store';
import {
  LabNamePicker,
  UnitPicker,
} from '@/documents/components/lab-row-editor';
import { CodePicker } from '@/documents/components/code-picker';
import type {
  DocumentTypeCode,
  PatientDocumentData,
  PatientDocumentFile,
  PatientDocumentLab,
  PatientDocumentModel,
} from '@/documents/types/document';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';

/**
 * NewFilePopup — Add-document side sheet (PROD-797 PR 2 of 6).
 *
 * Mirrors Flutter `new_file_popup_widget.dart` + the simpler
 * sub-form variants (Notes / Form / Admin Referrals). Lab / Procedure
 * / Radiology sub-forms ship in PRs 3-4; this popup hides their
 * options from the type picker for V1.
 *
 * Flow for the THREE TYPES this PR supports:
 *
 *   Notes / Admin Referrals (identical UX):
 *     1. Pick type
 *     2. Name (required) + Document date (required, defaults today)
 *     3. Optional metadata: doctor name, facility name, tags
 *     4. Attach 1+ files (drag-drop or browse)
 *     5. Save → upload each file → AddUpdate with collected URLs
 *
 *   Form:
 *     1. Pick type = Form
 *     2. Dropdown: org form templates (useOrganizationForms)
 *     3. Download link for the picked template (blank PDF / DOC for
 *        the user to fill offline)
 *     4. Document date (required) + optional metadata
 *     5. Attach the filled file
 *     6. Save → upload → AddUpdate
 *
 * Two-step submit:
 *   a) Upload each File via useUploadDocument → collect signed URLs
 *   b) Build PatientDocumentModel with patientDocumentFiles + ship
 *      via useSaveDocument
 *
 * `data: { title: null, code: null }` ships verbatim on every save
 * even when unused — per the dev curl shape (PROD-797 PR 1 contract).
 */

interface NewFilePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Optional encounter scope; ships through to AddUpdate as-is. */
  encounterId?: string | null;
  /**
   * PROD-2374 — the appointment this document is being filed against.
   *
   * OPTIONAL BY DESIGN. This drawer is shared by five surfaces and only the
   * Technician Worklist files against a specific session; everywhere else a
   * document belongs to the patient (or an encounter) and there is no honest
   * appointment to name. A caller that omits this ships
   * `appointmentId: null`, which is exactly what the column held for every
   * document written before this ticket — so no existing consumer changes
   * behaviour, and no consumer is at risk of stamping a session link it
   * cannot justify.
   *
   * Why it matters when it IS passed:
   * `POST /api/Patients/GetDocumentsByAppointments` — the worklist's report
   * feed — filters on `PatientDocument.AppointmentId`. Without this the
   * write never sets the column, the read can never match, and a completed
   * session shows "no report" after a reload however many reports were
   * attached to it. The read was built first; this is the write that makes
   * it able to find anything.
   *
   * Edit mode does NOT take this prop: `initialDocument` is spread first and
   * already carries the row's own `appointmentId`, so an edit preserves the
   * link rather than re-deciding it.
   */
  appointmentId?: string | null;
  /**
   * Lock the doc-type picker to a single type. When set, the type-tabs
   * row is hidden and the popup starts on (and stays on) the locked
   * type. Used by the Lab Linkage Order Requests tab to reuse this
   * shared popup for the result-upload flow but constrained to the
   * Labs tab (Andrew direction 2026-05-21).
   */
  lockedType?: DocumentTypeCode;
  /**
   * PROD-2374 — NARROW the doc-type picker without removing it.
   *
   * `lockedType` above is the one-type case: the tab row disappears and the
   * whole drawer switches to the Lab-Linkage chrome. This is the several-types
   * case: the tab row STAYS, interactive, showing only these types, and the
   * drawer opens on `allowedTypes[0]`. Used by the Technician Worklist, where
   * a session report is either a Radiology or a Procedure document depending
   * on what the device produced and only the technician knows which
   * (`SESSION_REPORT_TYPES` in `@/scheduling/lib/session-report-types`).
   *
   * Omit it and nothing moves — each of the other four callers renders the
   * full six-type row exactly as before. `lockedType` WINS if both are somehow
   * set, and EDIT mode ignores the narrowing outright: the row is display-only
   * there and has to be able to show the document's real type, which need not
   * be one of these.
   *
   * Pass a module-level constant, not an inline literal. The restriction on
   * the WRITE has to agree with the `fileType` filter of whatever READS those
   * documents back, and a literal at each end is how the two silently drift
   * apart — a document that saves fine and can never be found again.
   */
  allowedTypes?: readonly DocumentTypeCode[];
  /**
   * PROD-2374 — render the ATTACH-A-FILE chrome instead of the
   * author-a-document chrome. Presentation only: it decides nothing about
   * which document types are on offer.
   *
   * `lockedType` used to carry this as a second, silent job. Setting it did two
   * unrelated things at once — restrict the picker to one type AND switch the
   * drawer to the compact "you are attaching one file to something that already
   * exists" layout: a wider sheet, the drop zone ABOVE the fields rather than
   * below them, the name field labelled "File Name", and Facility alone instead
   * of Doctor name + Tags. The Technician Worklist wanted the second job and was
   * paying for it with the first; when the type restriction was widened to two
   * types (`allowedTypes`) the layout silently went with it.
   *
   * So the two are separate props now. Pass this to get the layout, pass
   * `lockedType` to get one type, pass both / either / neither — they no longer
   * imply one another. `lockedType` still turns this layout on by itself, so
   * the Order Requests tab (the only caller that sets it) renders exactly as
   * before and never needs to know this prop exists.
   *
   * NOT what this controls: one-file-per-document. That is unconditional on
   * every surface and has been since 2026-06-10 (`addFiles` keeps only
   * `accepted[0]`, the input is `multiple={false}`, and the drop zone hides
   * once a file is attached). It was never conditioned on `lockedType` and is
   * not conditioned on this. Do not make it conditional here: "one document =
   * one file" is a rule about documents, not about one caller's chrome.
   */
  attachFileLayout?: boolean;
  /**
   * Files already chosen by the caller, listed as if picked here (PROD-3204 lab desk: a report uploaded with reading
   * off is attached as today, without picking it a second time). The caller checks type and size first.
   */
  initialFiles?: File[];
  /**
   * Optional callback fired AFTER `useSaveDocument` succeeds and BEFORE
   * the popup closes. The save response is the new document id as a
   * bare string (curl-4 wire shape, verified 2026-05-21). Consumers
   * chain follow-up mutations here (e.g. lab-order linkage via
   * `useLinkLabResult`).
   *
   * If `onSaved` throws, the popup STAYS OPEN — same retry-friendly
   * pattern as the upload-failure path. The caller is expected to
   * have surfaced an error toast.
   */
  onSaved?: (documentId: string) => Promise<void> | void;
  /**
   * PROD-899 — when set, the popup opens in EDIT mode pre-populated
   * from this row. Save dispatches the same
   * `POST /api/Patients/AddUpdatePatientDocument` with `id` populated
   * so the backend treats it as an upsert.
   *
   * Hydrated state covers every editable field — name, dates, doctor
   * / facility / tags, code picker (Procedure / Radiology), lab rows,
   * and the document's currently-attached files. Server-set audit
   * fields (`createdById`, `reviewedOn`, etc.) ride along verbatim
   * via spread so the round-trip preserves review state.
   *
   * In edit mode the type-tabs row is hidden — switching type
   * mid-edit isn't supported in Flutter and breaks code-driven
   * forms; the existing row's type stays put.
   */
  initialDocument?: PatientDocumentModel;
  /**
   * PROD-1051 doc-forms — route by template mode. When the user picks a published
   * **document-mode** consent template in the Form flow, this fires instead of the
   * download-blank-and-reupload path; the parent opens the in-app fill-and-sign popup.
   * HTML / legacy org-form templates keep the existing offline download flow.
   */
  onPickConsentTemplate?: (template: ConsentTemplate) => void;
  /**
   * PROD-1243 (Case 3) — the consent was signed OUTSIDE the app. When the caller supports
   * it, the Form-template picker shows a "Sign in app / Upload signed PDF" toggle; picking a
   * template in upload mode fires this (instead of `onPickConsentTemplate`) so the parent
   * opens the external-upload dialog. Omit to hide the toggle (in-app fill only).
   */
  onUploadSignedConsent?: (template: ConsentTemplate) => void;
  /**
   * Initial value for the document name. The worklist passes the session's
   * product name so the technician does not retype what the row already says.
   * Only seeds the field — the user can still change it, and it must not
   * fight `initialDocument` (edit mode), which wins.
   */
  defaultName?: string;
}

/**
 * PROD-901 / PROD-902 file checks, shared by a pick and by `initialFiles`: the type allowlist (with the MIME guard)
 * and the user's upload limit, in MB.
 */
function checkDocumentFiles(
  files: File[],
  maxUploadMb: number,
): { accepted: File[]; badExtNames: string[]; oversizeNames: string[] } {
  const accepted: File[] = [];
  const badExtNames: string[] = [];
  const oversizeNames: string[] = [];
  for (const f of files) {
    if (!isValidExtension(f.name) || !isMimeConsistent(f)) badExtNames.push(f.name);
    else if (isFileSizeGreater(f.size, maxUploadMb)) oversizeNames.push(f.name);
    else accepted.push(f);
  }
  return { accepted, badExtNames, oversizeNames };
}

interface FileRow {
  /** Local id so React keys don't desync if a user removes mid-upload. */
  localId: string;
  /**
   * The `File` instance to upload. When the user renames the file in
   * the picker (PROD-903), we rebuild this with `new File([file],
   * newName, {type, lastModified})` so `file.name` reflects the new
   * value AND the upload's multipart `filename=` header ships the
   * renamed value.
   */
  file: File;
}

/**
 * Format a date as Flutter's `scDateFormat` default (`'MMM dd, yyyy'`,
 * e.g. `"May 23, 2026"`). Used by the Lab-name auto-default (PROD-904).
 * Mirrors Flutter `scrips_core/lib/utils/utils.dart:176`.
 */
function formatScDate(d: Date): string {
  // Intl gives a stable "May 23, 2026" without locale-specific punctuation.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Strip extension from a filename (e.g. "report.pdf" → "report"). */
function stripExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx > 0 ? filename.slice(0, idx) : filename;
}

/**
 * Doc-type tabs row at the top of the popup. Order + icons mirror the
 * Flutter "New File" widget: Note · Form · Lab · Radiology · Procedure ·
 * Admin & Referrals. Icons match the row-action icons in `document-row.tsx`
 * (same `iconFor` map) so the popup tab + the resulting list row share
 * one visual identity.
 */
const TYPE_OPTIONS_V1: {
  value: DocumentTypeCode;
  label: string;
  icon: LucideIcon;
}[] = [
  { value: DocumentType.Notes, label: 'Note', icon: FileText },
  { value: DocumentType.Forms, label: 'Form', icon: ClipboardList },
  { value: DocumentType.Labs, label: 'Lab', icon: FlaskConical },
  { value: DocumentType.Radiology, label: 'Radiology', icon: Scan },
  { value: DocumentType.Procedure, label: 'Procedure', icon: Stethoscope },
  {
    value: DocumentType.AdminReferrals,
    label: 'Admin & Referrals',
    icon: FileSpreadsheet,
  },
];

/**
 * Document types whose `name` field is sourced from the picked code's
 * display string (NOT a free-text input). Used by the validation +
 * submit-body logic.
 */
const CODE_DRIVEN_TYPES: DocumentTypeCode[] = [
  DocumentType.Procedure,
  DocumentType.Radiology,
];

/** Build an empty lab row template — local-only id for React keys. */
function newLabRow(): PatientDocumentLab {
  return {
    id: null,
    patientDocumentId: null,
    labName: null,
    result: '',
    unitType: null,
    isDeleted: false,
  };
}

/**
 * Per-row React key (Samer review PR #139 §2). Generated ONCE at row
 * creation via `crypto.randomUUID()` and never reassigned, so adding /
 * removing rows mid-list doesn't make React reuse the wrong editor
 * state. NOT array-index-based — those would collide when a middle
 * row is removed and the trailing rows shift down.
 *
 * Falls back to a counter for the (vanishingly small) chance the
 * runtime doesn't expose `crypto.randomUUID` — every modern browser
 * does, including Safari 15.4+.
 */
let __labRowKeyFallbackSeq = 0;
function nextLabRowKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  __labRowKeyFallbackSeq += 1;
  return `lab-row-fallback-${__labRowKeyFallbackSeq}`;
}

function todayLocalIso(): string {
  // `YYYY-MM-DDTHH:mm:ss.sss` — no Z (matches the dev-curl wire
  // contract). Built off Date components instead of toISOString().
  const d = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function dateInputToWire(value: string): string {
  // <input type="date"> gives `YYYY-MM-DD`; append a midnight local
  // time + millis to match the wire shape (`...T00:00:00.000`).
  return value ? `${value}T00:00:00.000` : '';
}

function wireToDateInput(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function extensionFor(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

/**
 * Force-download a cross-origin URL with the desired filename. The
 * standard `<a download>` attribute is silently ignored for
 * cross-origin hrefs (Azure-blob URLs always are), so we fetch the
 * resource ourselves, wrap it in an ObjectURL, and synthesise an
 * anchor click. Falls back to opening in a new tab if the fetch
 * fails (CORS, expired SAS, network).
 */
async function downloadCrossOrigin(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on next tick so the click handler can still consume the
    // ObjectURL — revoking synchronously cancels the download in Safari.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  } catch {
    // Fallback: open the original URL in a new tab. The user can
    // right-click → save from there if the browser renders inline.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Resolve a downloadable URL off an org form-template row.
 * `/api/Patients/OrganizationForm` ships the SAS-signed blob URL on
 * **`formPath`** (curl-verified 2026-06-10 — see
 * `use-organization-forms.ts`). The remaining names are kept as
 * defensive fallbacks in case a future backend rename lands.
 */
function resolveFormTemplateUrl(
  f: Record<string, unknown> | undefined | null,
): string | null {
  if (!f) return null;
  for (const k of ['formPath', 'url', 'fileUrl', 'documentUrl', 'path']) {
    const v = f[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/**
 * Download filename for a form template. The wire has no filename
 * field, so we derive it from the `formPath` blob path (which carries
 * the real `*.pdf` name), stripping the SAS query string. Falls back
 * to `formName` when the path can't be parsed.
 */
function resolveFormTemplateFilename(
  f: Record<string, unknown> | undefined | null,
): string {
  if (f) {
    const url = resolveFormTemplateUrl(f);
    if (url) {
      try {
        const pathname = new URL(url).pathname;
        const last = decodeURIComponent(
          pathname.slice(pathname.lastIndexOf('/') + 1),
        );
        if (last) return last;
      } catch {
        // Not a parseable absolute URL — fall through to the name fields.
      }
    }
    for (const k of ['fileName', 'formName', 'name']) {
      const v = f[k];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return 'template';
}

/**
 * PROD-1051 doc-forms — the "Form template" dropdown lists published document-mode consent
 * templates; picking one routes to the parent's in-app fill-and-sign popup (no offline
 * download). This REPLACES the legacy org-form-template values in this dropdown for any
 * caller that supports fill-and-sign (Andrew 2026-06-17). Isolated in its own component so
 * `useConsentTemplates` only runs when the Form type is active AND the parent opted in.
 */
function InAppConsentFormPicker({
  onPick,
  onUploadExternal,
}: {
  onPick: (t: ConsentTemplate) => void;
  /** When provided, a "Sign in app / Upload signed PDF" toggle appears; upload mode routes here. */
  onUploadExternal?: (t: ConsentTemplate) => void;
}) {
  const { data, isLoading, isError } = useConsentTemplates();
  const [mode, setMode] = useState<'in-app' | 'external'>('in-app');
  const signable = (data ?? []).filter(
    (t) => t.status === 'published' && isDocumentTemplate(t),
  );
  const external = mode === 'external' && !!onUploadExternal;
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-text-muted">
        Form template
      </label>
      {onUploadExternal ? (
        // PROD-1243: choose how the form is signed before picking the template.
        <div className="inline-flex rounded-md bg-grey-100 p-1">
          {(['in-app', 'external'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                'h-8 px-3 rounded-md text-size-compact font-medium transition-colors',
                mode === m
                  ? 'bg-surface-card text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {m === 'in-app' ? 'Sign in app' : 'Upload signed PDF'}
            </button>
          ))}
        </div>
      ) : null}
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading templates…</p>
      ) : isError ? (
        <p className="text-sm text-status-error">Couldn't load form templates.</p>
      ) : signable.length === 0 ? (
        <p className="text-sm text-text-muted">
          No form templates configured for this organization.
        </p>
      ) : (
        // value="" — picking a template immediately routes (this popup closes), so there's
        // no persistent selection to track here.
        <Select
          value=""
          onValueChange={(v) => {
            const t = signable.find((x) => x.id === v);
            if (!t) return;
            if (external) onUploadExternal!(t);
            else onPick(t);
          }}
        >
          <SelectTrigger className="w-full">
            {/* base-ui Select.Value renders the raw value (the id); map id → name via the
                function child (see feedback_baseui_selectvalue_label). */}
            <SelectValue
              placeholder={external ? 'Pick the form that was signed…' : 'Pick a form template…'}
            >
              {(value) => signable.find((x) => x.id === value)?.name ?? null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {signable.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export function NewFilePopup({
  open,
  onOpenChange,
  patientId,
  encounterId,
  appointmentId,
  lockedType,
  allowedTypes,
  attachFileLayout,
  initialFiles,
  onSaved,
  initialDocument,
  onPickConsentTemplate,
  onUploadSignedConsent,
  defaultName,
}: NewFilePopupProps) {
  // PROD-899: edit mode is signalled by `initialDocument` being set.
  // The popup is then pre-populated from the row and Save dispatches
  // an upsert with the existing `id`. Type-switching is disabled
  // (Flutter parity — the existing row's type is what gets saved).
  const isEditMode = !!initialDocument;
  // The type this drawer lands on with nothing else to go on. `lockedType` is
  // the only type there is; `allowedTypes` narrows the row, so its FIRST entry
  // is both the first tab and the starting tab; otherwise Notes, as before.
  // Scalar, so using it as an effect dep costs nothing.
  const defaultType: DocumentTypeCode =
    lockedType ?? allowedTypes?.[0] ?? DocumentType.Notes;
  const [docType, setDocType] = useState<DocumentTypeCode>(
    (initialDocument?.type as DocumentTypeCode | undefined) ?? defaultType,
  );
  // Refs for the file-type tab buttons so the arrow-key roving handler
  // can focus the next tab after switching. Samer review PR #159 §2.
  const typeTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [name, setName] = useState('');
  // PROD-2007: has the `defaultName` seed already had its one go THIS open?
  // A ref, not derived state — see the seed effect for why "the field is
  // empty" is the wrong test.
  const seededDefaultNameRef = useRef(false);
  const [documentDate, setDocumentDate] = useState(() =>
    wireToDateInput(todayLocalIso()),
  );
  const [doctorName, setDoctorName] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [tags, setTags] = useState('');
  const [pickedFormId, setPickedFormId] = useState<string>('');
  // PROD-3204: files the caller already chose pass the same type and size checks as a pick (the user's
  // `maxDocumentUploadLimit`). A refused one is never listed; the toast below says why.
  const initialFilesLimit = useAuthStore((st) => st.user?.maxDocumentUploadLimit);
  const [initialCheck] = useState(() =>
    checkDocumentFiles(initialFiles ?? [], initialFilesLimit ?? DEFAULT_DOCUMENT_MAX_UPLOAD_MB),
  );
  const [files, setFiles] = useState<FileRow[]>(() =>
    initialCheck.accepted.slice(0, 1).map((file, i) => ({ localId: `initial-${i}-${file.name}-${file.size}-${file.lastModified}`, file })),
  );
  // Lab-row state keyed by stable per-row local id so React keys don't
  // collide. The wire payload strips the key before send.
  //
  // `isEditing` mirrors Flutter `PatientDocumentLab.isEdit` — a
  // transient UI flag (NOT shipped on the wire). When true the row
  // renders the full editor; when false it renders a read-only summary
  // card with Edit / Delete icons. Only used by locked-mode (Lab
  // Linkage). Unlocked mode keeps the always-editable inline pattern
  // and ignores this flag.
  const [labRows, setLabRows] = useState<
    Array<{ key: string; row: PatientDocumentLab; isEditing: boolean }>
  >([]);
  // Procedure / Radiology types pick a code from the master catalogue.
  // The picked value populates `data.title` + `data.code` on the body.
  const [pickedCode, setPickedCode] = useState<PatientDocumentData | null>(null);
  // PROD-899 edit-mode: files already attached to the row before the
  // edit started. Distinct from `files` (fresh uploads added during
  // this session). On Save we union both — existing files ship
  // verbatim (with optional `isDeleted: true` overlay when the user
  // removes them) and new uploads ship after their POST resolves.
  const [existingFiles, setExistingFiles] = useState<PatientDocumentFile[]>(
    [],
  );
  // PROD-903: per-file rename state. Tracks which file row is in edit
  // mode (null = none) and the in-progress draft name. Commit rebuilds
  // the underlying File with the new name.
  const [renamingLocalId, setRenamingLocalId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Stable id linking the drop-zone <label> to the hidden file <input>.
  // We open the picker via native label association (htmlFor) rather than
  // a programmatic `inputRef.click()` — the JS click is fragile inside a
  // nested base-ui Sheet focus-trap and under the local-dev SignalR
  // reconnect storm (it would silently no-op → "Browse not working").
  // A native label is a trusted gesture the browser always honours.
  const fileInputId = useId();
  const [dragActive, setDragActive] = useState(false);

  const upload = useUploadDocument();
  const save = useSaveDocument();
  const orgForms = useOrganizationForms({ enabled: open && docType === DocumentType.Forms });

  // PROD-902: org-level max upload limit (MB). Mirrors
  // `add-file-observation.tsx:65-67` pattern.
  //
  // Samer PR #339 review item 1: a missing org config must be LOUD,
  // not silently permissive at the Flutter-default 10 MB. We surface
  // both an inline banner (visible in the popup) and a console.error
  // so ops triage can find the misconfigured tenant. The fallback
  // limit is preserved (Flutter parity — Flutter ships 10 when null)
  // BUT the missing-config state is no longer silent. Users get a
  // chance to abort + contact support before committing PHI.
  //
  // **Backend dependencies** (Samer review items 2 + 5):
  // - PROD-938: the .NET `UploadController` is the authoritative
  //   HIPAA gate. Client-side checks here are defense-in-depth —
  //   they reduce blast radius (orphan blob writes, bandwidth, UX)
  //   but cannot be the sole guard. Backend ticket tracks server-
  //   side enforcement audit (extension allowlist + size + org-id
  //   binding).
  // - PROD-939: `maxDocumentUploadLimit` org-scope correctness
  //   depends on the backend assembling the JWT `UserData` row from
  //   the user's active practice/org row, not a global default.
  //   Backend ticket tracks tenant-isolation audit.
  const authUser = useAuthStore((s) => s.user);
  const orgUploadLimit = authUser?.maxDocumentUploadLimit;
  const uploadLimitMissing =
    orgUploadLimit === null || orgUploadLimit === undefined;
  const maxUploadMb = orgUploadLimit ?? DEFAULT_DOCUMENT_MAX_UPLOAD_MB;

  // PROD-3204: say why a file the caller passed in (`initialFiles`) was not listed. A toast only, once per open.
  useEffect(() => {
    if (initialCheck.badExtNames.length > 0) toast.error(TOAST_DOCUMENT_FILE_INVALID);
    if (initialCheck.oversizeNames.length > 0) toast.error(oversizeMessage(maxUploadMb));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the check ran once, when the popup opened
  }, [initialCheck]);

  // Warn once per popup-open when the org config is missing. Fires on
  // initial mount AND on every reopen so the warning isn't suppressed
  // by a stale-state silence.
  useEffect(() => {
    if (!open) return;
    if (!uploadLimitMissing) return;
    console.error(
      '[NewFilePopup] maxDocumentUploadLimit is missing on auth-store.user. ' +
        'Falling back to ' +
        DEFAULT_DOCUMENT_MAX_UPLOAD_MB +
        ' MB. Bad org config — see PROD-902 / Samer #339 review.',
    );
  }, [open, uploadLimitMissing]);

  // PROD-939 runtime observability — log the (organizationId,
  // practiceId, maxDocumentUploadLimit) triple on popup open so ops
  // can correlate the limit against the active org in dev/Sentry. If
  // a wrong-org limit ever ships, the log makes it findable in the
  // session that triggered it. Logs on the (a) initial open AND
  // (b) every org/practice/limit change while open (e.g. JWT
  // refresh mid-session).
  //
  // **Log level = `console.debug`** (Samer review #3): Sentry's
  // default `Breadcrumbs` integration captures log/info/warn/error
  // breadcrumbs but excludes `debug` by default — and DevTools tiers
  // `debug` into a separate filter group. This keeps the
  // observability greppable when ops needs it (filter on
  // `[NewFilePopup] tenant-scope context` in DevTools or attach a
  // Sentry integration that opts into debug breadcrumbs) without
  // polluting prod Sentry dashboards. If a future Sentry config
  // enables debug breadcrumbs globally, add a per-prefix exclude on
  // `[NewFilePopup]` at the Sentry integration layer rather than
  // downgrading further here.
  useEffect(() => {
    if (!open) return;
    console.debug(
      '[NewFilePopup] tenant-scope context',
      JSON.stringify({
        organizationId: authUser?.organizationId ?? null,
        practiceId: authUser?.practiceId ?? null,
        maxDocumentUploadLimit: orgUploadLimit ?? null,
      }),
    );
  }, [
    open,
    authUser?.organizationId,
    authUser?.practiceId,
    orgUploadLimit,
  ]);

  // Open / close lifecycle.
  //
  // - **Close**: reset every field to the unmounted defaults so a
  //   re-open starts clean. The type snaps back to `defaultType` — the
  //   locked type (Lab Linkage), else the first allowed type (Technician
  //   Worklist), else Notes.
  // - **Open in EDIT mode** (PROD-899): hydrate every field from
  //   `initialDocument`. Type tab row is hidden in edit mode (see
  //   render block below) so the user can't switch type mid-edit.
  //
  // We deliberately exclude `save` / `upload` from the dep array per
  // the Storybook + React 19 + StrictMode rerun edge-case documented
  // below (Minified React error #185 — "Too many re-renders").
  // Including them would re-call `.reset()` on every render, burning
  // React's 50-iter render budget on the `Closed` story.
  useEffect(() => {
    if (!open) {
      // Close path — reset to defaults.
      setDocType(defaultType);
      setName('');
      setDocumentDate(wireToDateInput(todayLocalIso()));
      setDoctorName('');
      setFacilityName('');
      setTags('');
      setPickedFormId('');
      setFiles([]);
      setLabRows([]);
      setPickedCode(null);
      setExistingFiles([]);
      // PROD-903: clear rename-in-progress state so a re-open starts
      // with no row in edit mode.
      setRenamingLocalId(null);
      setRenameDraft('');
      upload.reset();
      save.reset();
      return;
    }
    // Open path. EDIT mode hydrates from `initialDocument`; ADD mode
    // is left in its default state (the close path already set it).
    if (initialDocument) {
      setDocType(
        (initialDocument.type as DocumentTypeCode | undefined) ??
          DocumentType.Notes,
      );
      setName(initialDocument.name ?? '');
      setDocumentDate(wireToDateInput(initialDocument.documentDate));
      setDoctorName(initialDocument.doctorName ?? '');
      setFacilityName(initialDocument.facilityName ?? '');
      setTags(initialDocument.tags ?? '');
      setPickedFormId('');
      setFiles([]);
      setExistingFiles(
        (initialDocument.patientDocumentFiles ?? []).filter(
          (f): f is PatientDocumentFile => !!f && !f.isDeleted,
        ),
      );
      // Code-driven types hydrate from `data.{title, code}`.
      if (
        initialDocument.type === DocumentType.Procedure ||
        initialDocument.type === DocumentType.Radiology
      ) {
        setPickedCode({
          title: initialDocument.data?.title ?? null,
          code: initialDocument.data?.code ?? null,
        });
      } else {
        setPickedCode(null);
      }
      // Lab rows ship in their non-editing summary card by default
      // (matches Flutter — user can click pencil to enter the editor
      // per row). Empty-tombstone rows are excluded so we don't
      // resurrect previously-deleted rows.
      setLabRows(
        (initialDocument.patientDocumentLabs ?? [])
          .filter((r): r is PatientDocumentLab => !!r && !r.isDeleted)
          .map((row) => ({
            key: nextLabRowKey(),
            row,
            isEditing: false,
          })),
      );
      upload.reset();
      save.reset();
    }
    // `save`/`upload` intentionally excluded (see the React #185 note above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDocument, defaultType]);

  // Lab rows use the Order-Requests (locked) flow on EVERY surface
  // (Andrew 2026-06-10): start with zero rows + an "Add Another Lab"
  // affordance — no auto-seeded row. (Previously the unlocked Documents
  // tab seeded one empty row; removed for parity with Order Requests.)

  // Locked-Lab default name — "Lab results YYYY-MM-DD" so the user
  // doesn't have to type one. Only seeds on open + only if the user
  // hasn't already typed.
  useEffect(() => {
    if (!open) return;
    if (lockedType !== DocumentType.Labs) return;
    if (name.trim().length > 0) return;
    setName(`Lab results ${documentDate}`);
    // `name`/`documentDate` read but excluded — seed once on open, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockedType]);

  // Clear the picked CPT / radiology code when switching AWAY from
  // a code-driven type. Avoids the body shipping stale `data` on a
  // subsequent Note / Form save.
  useEffect(() => {
    if (!CODE_DRIVEN_TYPES.includes(docType)) {
      setPickedCode(null);
    }
  }, [docType]);

  const isCodeDrivenType = CODE_DRIVEN_TYPES.includes(docType);

  // PROD-2374 — the ATTACH-A-FILE chrome. Read this for every layout decision
  // below; read `lockedType` only where the question is genuinely "is there
  // exactly one document type here?".
  //
  // `lockedType` implies it, which is what keeps the Order Requests tab
  // (the only caller setting `lockedType`) rendering exactly as it did — it
  // passes nothing new and loses nothing. `attachFileLayout` is how a caller
  // asks for the same chrome WITHOUT collapsing the picker to one type, which
  // is the Technician Worklist: two types on offer, one file attached.
  const usesAttachLayout = !!lockedType || !!attachFileLayout;

  // PROD-2374 — which tabs the row actually offers. `allowedTypes` narrows it
  // (order kept from TYPE_OPTIONS_V1, so the row reads the same left-to-right
  // as everywhere else); omitted, it is the full six as before. EDIT mode opts
  // out: that row is display-only and must be able to show the document's own
  // type, which a caller's narrowing has no business hiding.
  const typeOptions =
    allowedTypes && !isEditMode
      ? TYPE_OPTIONS_V1.filter((o) => allowedTypes.includes(o.value))
      : TYPE_OPTIONS_V1;

  // Is the row rendering FEWER than the full six? (PROD-2406; Andrew,
  // 2026-09-21.)
  //
  // Derived from what is actually being rendered, NOT from `allowedTypes`
  // being set: edit mode opts out of the narrowing above, so the prop and the
  // row disagree there, and it is the row's own width this decides. A caller
  // that "narrows" to all six lands on the six-tab layout, correctly.
  //
  // WHAT IT IS FOR. The row is `justify-between` over fixed 60px tabs, which
  // distributes whatever it holds across the full sheet. With six tabs that
  // reads as a tab strip. With two — the Technician Worklist's Radiology +
  // Procedure — it pins one option to the left edge and the other to the
  // right with a gap between them wider than both controls put together, and
  // they stop reading as one two-way choice: "put the radiology and the
  // procedure selection beside each others as right now they are not."
  //
  // The narrowed row therefore groups at the start instead of distributing.
  // Scoped to the narrowed case ON PURPOSE: `NewFilePopup` is shared by five
  // surfaces and the other four render all six tabs, so the six-tab branch
  // below is byte-for-byte the class string it has always been.
  const isNarrowedTypeRow = typeOptions.length < TYPE_OPTIONS_V1.length;

  // Switch the active document type AND clear every per-type selection /
  // entry (audit 2026-06-09). Without this, a value from one type bleeds
  // into the next — e.g. a Lab auto-name showing on the Note tab, or a
  // picked Procedure code persisting after switching to Radiology (both are
  // code-driven, so the "clear on leaving code-driven" effect doesn't fire).
  // Type tabs are hidden in edit mode, so this never wipes hydrated values.
  const handleTypeChange = (next: DocumentTypeCode) => {
    if (next === docType) return;
    setName('');
    setDoctorName('');
    setFacilityName('');
    setTags('');
    setPickedCode(null);
    setPickedFormId('');
    setLabRows([]);
    setDocType(next);
  };

  // PROD-904: Lab name auto-default. Flutter `lab_document_widget.dart:50-52`
  // sets `name = 'Lab results ${scDateFormat(DateTime.now())}'` on init.
  // We mirror that for the unlocked Documents tab — only when the user
  // hasn't typed anything yet. The existing locked-mode (Lab Linkage)
  // default at the open-effect below stays put.
  useEffect(() => {
    if (!open) return;
    if (lockedType) return; // locked-mode default handled in open-effect
    if (docType !== DocumentType.Labs) return;
    if (name.trim().length > 0) return;
    setName(`Lab results ${formatScDate(new Date())}`);
    // `name` read but excluded — seed once when the Labs tab opens, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docType, lockedType]);

  // PROD-2007: caller-supplied name seed. The Technician Worklist opens this
  // drawer from a session that already names its product, so the name arrives
  // prefilled rather than being retyped.
  //
  // SEED ONLY, and it loses every argument it could have:
  //   - edit mode wins outright (`initialDocument` hydrates the real document's
  //     name in the open-effect above; a seed must never rewrite it);
  //   - a name the user has already typed wins (same `name.trim()` guard the
  //     Lab auto-defaults use);
  //   - existing consumers pass nothing, so nothing about their behaviour moves.
  //
  // Deliberately declared AFTER the two Lab auto-default effects: a caller that
  // supplies both a `defaultName` and the Labs type gets the specific product
  // name, not the generic "Lab results <date>", because within one effect pass
  // both read the same empty `name` and the last writer wins.
  //
  // LIMIT OF THAT ORDERING — it holds on the FIRST open only. The Lab effect
  // above re-fires on `docType`; this one does not. So if the user opens the
  // drawer and then switches the type tab to Labs, `handleTypeChange` clears
  // `name`, the Lab effect runs against the now-empty field and the generic
  // "Lab results <date>" wins — the reverse of the first-open precedence. No
  // caller can reach that today: the only consumer passing `defaultName` is
  // the Technician Worklist, whose `allowedTypes` is Radiology + Procedure, so
  // Labs is not a tab it can reach. (It used to be unreachable for a different
  // reason — the worklist passed `lockedType` and had no tabs at all — so check
  // the tabs, not the prop, if you revisit this.) Recorded rather than fixed because the fix
  // is restructuring five sibling seeds in a component four screens depend on.
  //
  // Re-opening with a different seed takes the new value: the close path clears
  // `name`, and the ref below is reset on close.
  useEffect(() => {
    if (!open) {
      // Reset on CLOSE, so the next open seeds again — that is what makes
      // "once per open" and not "once per mount".
      seededDefaultNameRef.current = false;
      return;
    }
    // Seed ONCE per open, keyed off a ref rather than off the field being
    // empty. The `name.trim()` guard below protects text the user has TYPED
    // but not text they have deliberately CLEARED: a technician who selects-
    // all-deletes the seeded "HBOT 60 min" to write their own title has an
    // empty field, and any later parent re-render carrying a different
    // `defaultName` (a worklist refetch changing the row's `profileName`) used
    // to re-run this effect and re-seed over them mid-thought.
    if (seededDefaultNameRef.current) return;
    if (initialDocument) return;
    const seeded = (defaultName ?? '').trim();
    if (!seeded) return;
    // Marked BEFORE the typed-text guard: an open that declined to seed
    // because the user was already writing has still had its one attempt, and
    // a later `defaultName` change must not get a second one.
    seededDefaultNameRef.current = true;
    if (name.trim().length > 0) return;
    // The input caps typing at 50 chars (PROD-906); a seed must not smuggle a
    // longer value past a limit the user cannot then exceed themselves.
    //
    // The write is disabled rather than restructured: the five sibling seeds in
    // this component all pre-date the rule and share the shape, and remounting
    // the drawer on a `key` (the pattern newer dialogs use) is a rewrite of a
    // component four other screens depend on, not a change this ticket may make.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setName(seeded.slice(0, 50));
    // `name` read but excluded — seed once on open, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultName, initialDocument]);

  // For Form type, pre-fill `name` from the picked template so the
  // user doesn't have to retype it.
  useEffect(() => {
    if (docType !== DocumentType.Forms) return;
    const picked = (orgForms.data ?? []).find((f) => f.id === pickedFormId);
    // Flutter binds the picked form's `formName` to the document `name`
    // (`forms_document_widget.dart`). Prefer `formName`, fall back to `name`.
    const formLabel = (picked?.formName as string | undefined) ?? picked?.name;
    if (formLabel) setName(formLabel);
  }, [docType, orgForms.data, pickedFormId]);

  const pickedTemplate = useMemo(
    () => (orgForms.data ?? []).find((f) => f.id === pickedFormId),
    [orgForms.data, pickedFormId],
  );

  // Per-row validity — Samer review PR #139 §3. Previously the filter
  // ("not fully empty") and the validation ("complete") were two
  // different thresholds, so a row with only a result filled would
  // pass the filter and silently ship a half-row to the backend.
  // Now they're aligned: a row is either DELETED (kept for tombstone)
  // OR COMPLETE (lab + result + unit, ships) OR INCOMPLETE (blocks
  // submit). No row is silently dropped.
  const rowValidity = useMemo(() => {
    return labRows.map((entry) => {
      const r = entry.row;
      if (r.isDeleted) return { key: entry.key, state: 'deleted' as const };
      const hasLab = !!r.labName?.code;
      const hasResult = !!String(r.result ?? '').trim();
      const hasUnit = !!r.unitType?.code;
      const isComplete = hasLab && hasResult && hasUnit;
      const isEmpty = !hasLab && !hasResult && !hasUnit;
      if (isComplete) return { key: entry.key, state: 'complete' as const };
      if (isEmpty) return { key: entry.key, state: 'empty' as const };
      return {
        key: entry.key,
        state: 'incomplete' as const,
        missing: {
          lab: !hasLab,
          result: !hasResult,
          unit: !hasUnit,
        },
      };
    });
  }, [labRows]);

  const incompleteRows = useMemo(
    () => rowValidity.filter((v) => v.state === 'incomplete'),
    [rowValidity],
  );

  // Submit rows: deleted (tombstone) + complete only. Empty + incomplete
  // are silently dropped only when they're empty (no user input); the
  // incomplete case is BLOCKED by canSubmit so we never reach the
  // mutation with a partial row.
  const submitLabRows = useMemo(
    () =>
      labRows
        .map((r, i) => ({ row: r.row, state: rowValidity[i]?.state }))
        .filter(({ state }) => state === 'deleted' || state === 'complete')
        .map((entry) => entry.row),
    [labRows, rowValidity],
  );

  // Lab rows use the Order-Requests (locked) rule on EVERY surface
  // (Andrew 2026-06-10): structured rows are OPTIONAL — the user is
  // uploading a result file; entering rows is a bonus, not a gate. A row
  // left in edit mode (or half-filled / incomplete) blocks Save (Flutter
  // sets `enableSaveButton: false` while a row is being added / edited;
  // clicking Done flips it back). No "≥1 complete row" requirement.
  const hasValidLabs = useMemo(() => {
    if (docType !== DocumentType.Labs) return true;
    const someEditing = labRows.some((r) => r.isEditing);
    return incompleteRows.length === 0 && !someEditing;
  }, [docType, incompleteRows.length, labRows]);

  // Validation rules by document type (Flutter `document_bloc.dart`:
  //   Notes / Forms / Admin Referrals: name + date required, files
  //     required (V1 enforces; Flutter's bloc validation allows
  //     fileless but our popup is upload-driven)
  //   Labs: name + date required, files required, ≥1 complete lab row
  //   Procedure / Radiology: data.code (CPT) + date required, files
  //     REQUIRED — same as every other type (PROD-1770; the comment that
  //     used to sit here claimed Flutter made them optional, which was a
  //     misreading of the bloc — see filesRequirementMet below)
  // The `name` field for Procedure / Radiology is auto-set from the
  // picked code's title at submit time; no free-text entry required.
  const nameRequirementMet = isCodeDrivenType
    ? !!pickedCode?.code
    : !!name.trim();
  // PROD-899: in edit mode, existing-files satisfy the "files required"
  // gate alongside any new uploads. A user editing metadata-only on a
  // doc that already has attachments shouldn't have to re-upload.
  const hasAnyVisibleFile =
    files.length > 0 || existingFiles.some((f) => !f.isDeleted);
  /**
   * PROD-1770(b) — was `isCodeDrivenType ? true : hasAnyVisibleFile`, letting a
   * Procedure or Radiology document save with nothing attached.
   *
   * The justification was a misreading of the Flutter bloc. `document_bloc.dart`
   * closes its per-type `if / else if` chain at line 80, then computes
   * `isFileAdded` at `:82-87` and ANDs it in at `:88` with no type test:
   *
   *     enableSaveButton = enableSaveButton && isFileAdded;
   *
   * The Procedure / Radiology branch (`:75-80`) sets only `data.code` and
   * `documentDate` — it never *relaxes* the file rule. So Flutter requires a file
   * for EVERY type; whoever read it saw the branch not mentioning files and
   * missed the unconditional AND two lines below.
   *
   * No error string is added, and that is deliberate rather than an oversight: a
   * sweep of the whole Flutter `document_module/` for attach / select-file copy
   * returns zero hits, and there is no matching `.arb` key. Flutter's only signal
   * is a greyed Save — and worse, `SlidingPopup`'s Done stays tappable
   * (`sliding_popup_view.dart:231-238` changes only the text colour) so its
   * handler silently no-ops. React's `disabled={!canSubmit}` is strictly better,
   * so it stays as the whole mechanism.
   */
  const filesRequirementMet = hasAnyVisibleFile;

  const canSubmit =
    nameRequirementMet &&
    !!documentDate &&
    filesRequirementMet &&
    !upload.isPending &&
    !save.isPending &&
    (docType !== DocumentType.Forms || !!pickedFormId) &&
    hasValidLabs;

  const addFiles = (incoming: FileList | File[]) => {
    // PROD-901 + PROD-902: enforce file-type allowlist + maxDocumentUploadLimit
    // BEFORE we add the file to upload state. Mirrors Flutter
    // `pick_document_widget.dart:131-180` (drop zone) and :210-272 (Browse).
    //
    // Samer PR #339 review item 3 (extension-spoofing guard): in addition
    // to the suffix check, the browser-reported MIME (`File.type`) is
    // validated against an extension-keyed allowlist. Files with an
    // empty MIME pass (browser couldn't sniff — Flutter parity). Files
    // with a populated MIME that disagrees with the extension are
    // rejected as part of the `badExtNames` category (same toast — the
    // user sees the same UX whether the bad input is a `.exe` or a
    // `.exe` masquerading as `.pdf`).
    //
    // Aggregates rejections so the user sees ONE toast per category
    // even when several files fail. Samer review item 4: valid files
    // in a mixed batch ARE added (see test
    // `accepts a mixed batch — keeps valid files…` for explicit cov.)
    const checked = checkDocumentFiles(Array.from(incoming), maxUploadMb);
    const { badExtNames, oversizeNames } = checked;
    const accepted: FileRow[] = checked.accepted.map((f) => ({
      localId: `${f.name}-${f.size}-${f.lastModified}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      file: f,
    }));

    if (badExtNames.length > 0) {
      toast.error(TOAST_DOCUMENT_FILE_INVALID);
    }
    if (oversizeNames.length > 0) {
      toast.error(oversizeMessage(maxUploadMb));
    }
    if (accepted.length > 0) {
      // Single-file on EVERY surface (Andrew 2026-06-10): one document =
      // one attached file. A fresh pick REPLACES the current selection
      // (and a multi-file drag keeps only the first accepted) rather than
      // appending. The drop zone is hidden once a file is present (see
      // `fileDropZone`), so the only way to change it is delete-then-add.
      setFiles([accepted[0]]);
    }
  };

  const removeFile = (localId: string) =>
    setFiles((prev) => prev.filter((f) => f.localId !== localId));

  // PROD-903 per-file rename helpers. Mirrors Flutter
  // `pick_document_widget.dart` `documentEditView` (lines 374-467):
  //   - Pencil enters edit mode for that row
  //   - Input value strips the extension; maxLength = 100 - extLen
  //   - Check commits: empty trim → toast "Please enter file name";
  //     else rebuild the File with `new File([blob], trimmed+ext)`
  //   - X cancels (state unchanged)
  const startRenameRow = (row: FileRow) => {
    setRenamingLocalId(row.localId);
    setRenameDraft(stripExtension(row.file.name));
  };

  const cancelRenameRow = () => {
    setRenamingLocalId(null);
    setRenameDraft('');
  };

  const commitRenameRow = (row: FileRow) => {
    const trimmed = renameDraft.trim();
    if (trimmed.length === 0) {
      // Verbatim Flutter `pick_document_widget.dart:444` toast.
      toast.error('Please enter file name');
      return;
    }
    const ext = extensionFor(row.file.name);
    const nextName = `${trimmed}${ext}`;
    if (nextName === row.file.name) {
      // No-op rename — just exit edit mode.
      cancelRenameRow();
      return;
    }
    const renamedFile = new File([row.file], nextName, {
      type: row.file.type,
      lastModified: row.file.lastModified,
    });
    setFiles((prev) =>
      prev.map((f) => (f.localId === row.localId ? { ...f, file: renamedFile } : f)),
    );
    cancelRenameRow();
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    try {
      // Step 1 — upload all files in parallel via Promise.allSettled
      // so we get a complete picture of which succeeded vs failed
      // before deciding whether to AddUpdate (Samer review PR #137 §2).
      //
      // Partial-failure semantics: if ANY upload fails, we bail BEFORE
      // calling AddUpdate. The successful uploads leave orphan blobs
      // in Azure storage — there is no client-side cleanup path here.
      // Reclassified 2026-05-21 (7th cross-cutting #8 instance): Flutter
      // `DeleteDocumentUseCase` is dead code — registered in DI at
      // `dependency_injection.dart:2053` but `DeleteDocumentEvent` doesn't
      // exist (no class, no dispatch site, no handler — grep-verified).
      // Both Flutter and React equally lack DeleteImage dispatch from
      // documents → MATCH-IDIOMATIC per "do as Flutter exactly". The
      // orphan-blob loophole is a HIPAA minimization concern flagged
      // for a separate cross-platform ticket (Andrew triage). Until
      // that ticket lands, we log the orphan URLs so ops can reconcile
      // against Azure storage metrics manually.
      //
      // Why allSettled over Promise.all: the latter rejects on the
      // first failure but still races the other uploads to completion
      // in the background, producing even more orphans. allSettled
      // gives us a clean accounting of every attempt.
      const results = await Promise.allSettled(
        files.map((row) => upload.mutateAsync({ file: row.file })),
      );

      const orphans: string[] = [];
      const uploaded: PatientDocumentFile[] = [];
      const failures: { name: string; reason: unknown }[] = [];
      results.forEach((result, idx) => {
        const row = files[idx];
        if (result.status === 'fulfilled') {
          uploaded.push({
            id: null,
            patientDocumentId: null,
            url: result.value.url,
            fileName: row.file.name,
            fileType: extensionFor(row.file.name),
            isDeleted: false,
          });
        } else {
          failures.push({ name: row.file.name, reason: result.reason });
        }
      });

      if (failures.length > 0) {
        // Collect successful URLs as orphans for ops triage. DeleteImage
        // from documents is dead code on Flutter (see partial-failure
        // header comment above for the cross-cutting #8 reclassification)
        // so client-side cleanup is out of scope until Andrew opens the
        // HIPAA-orphan-blob ticket that covers Flutter AND React equally.
        // Until that lands, these blobs persist until manual cleanup.
        uploaded.forEach((u) => u.url && orphans.push(u.url));
        console.error('[NewFilePopup] partial upload failure', {
          failed: failures.map((f) => f.name),
          orphanBlobs: orphans,
        });
        toast.error(
          `Couldn't upload ${failures.length} of ${files.length} file${files.length === 1 ? '' : 's'}. Try again.`,
        );
        return;
      }

      // Step 2 — AddUpdate the document with the uploaded URLs.
      //
      // Code-driven types (Procedure / Radiology) get their `name` AND
      // `data.{title, code}` from the picked master row. Flutter does
      // the same: the procedure widget builds `Data(title: ..., code:
      // ...)` from the picked CodeList and the body's `name` mirrors
      // the title for display in the list. Non-code-driven types ship
      // `name` from the free-text input and `data: { title: null,
      // code: null }` (wire-canonical per the dev curl).
      const wireName = isCodeDrivenType
        ? pickedCode?.title ?? pickedCode?.code ?? ''
        : name.trim();
      const wireData: PatientDocumentData =
        isCodeDrivenType && pickedCode
          ? { title: pickedCode.title ?? null, code: pickedCode.code ?? null }
          : { title: null, code: null };

      // PROD-899: in edit mode, spread the existing document FIRST so
      // server-set audit fields (createdById, createdOn, reviewedById,
      // reviewedByName, reviewedOn, updatedById, updatedByName, updatedOn,
      // and the row `id`) ride along verbatim. Then overlay the
      // user-edited fields on top.
      //
      // Existing files: ship the existingFiles state as-is. The user
      // can mark items deleted via the `existingFiles` row UI, which
      // sets `isDeleted: true` on the entry. The backend treats
      // patientDocumentFiles as the source of truth on save and
      // tombstones the marked entries.
      //
      // New uploads merge in after the existing list.
      const mergedFiles: PatientDocumentFile[] = [
        ...existingFiles,
        ...uploaded,
      ];

      const body: PatientDocumentModel = initialDocument
        ? {
            ...initialDocument,
            // Overlay user-edited fields. Type stays put per design
            // (tab row hidden in edit mode), so re-take from existing.
            name: wireName,
            type: initialDocument.type ?? docType,
            documentDate: dateInputToWire(documentDate),
            doctorName: doctorName.trim() || null,
            facilityName: facilityName.trim() || null,
            tags: tags.trim() || null,
            data: wireData,
            // `isDeleted` is the archive flag (PROD-900) — keep
            // whatever the row had. Edit isn't archive.
            isDeleted: initialDocument.isDeleted ?? false,
            patientDocumentFiles: mergedFiles,
            patientDocumentLabs:
              docType === DocumentType.Labs ? submitLabRows : [],
          }
        : {
            id: null,
            patientId,
            // Explicit `?? null` (Samer review PR #137 §4): the prop is
            // `string | null | undefined`. JSON.stringify drops `undefined`
            // values entirely, which the .NET model binder may reject as
            // a missing field. Coercing to `null` matches the dev-curl
            // wire-shape contract (`"encounterId": null` ships present).
            encounterId: encounterId ?? null,
            // PROD-2374 — the session↔report link, and the same `?? null`
            // contract as `encounterId` above: the prop is
            // `string | null | undefined`, and `JSON.stringify` would drop an
            // `undefined` field entirely rather than shipping it as null.
            //
            // A caller that files against the patient rather than a session
            // passes nothing and this ships `null` — the pre-PROD-2374
            // behaviour, unchanged. A caller that DOES name a session (the
            // Technician Worklist) is the only reason
            // `GetDocumentsByAppointments` can ever return that document:
            // the endpoint filters on this column, so a NULL here is a report
            // its own session will never find.
            appointmentId: appointmentId ?? null,
            name: wireName,
            type: docType,
            documentDate: dateInputToWire(documentDate),
            doctorName: doctorName.trim() || null,
            facilityName: facilityName.trim() || null,
            tags: tags.trim() || null,
            reviewedById: null,
            reviewedByName: null,
            reviewedOn: null,
            data: wireData,
            createdById: null,
            createdByName: null,
            createdOn: null,
            updatedById: null,
            updatedByName: null,
            updatedOn: null,
            isDeleted: false,
            patientDocumentFiles: mergedFiles,
            // Lab rows ship only for the Labs type; other types send the
            // empty array per the dev-curl wire contract.
            patientDocumentLabs:
              docType === DocumentType.Labs ? submitLabRows : [],
          };

      // useSaveDocument returns the new document id as a bare string
      // (curl-4 wire shape). Pass it straight through to onSaved.
      const documentId = await save.mutateAsync(body);
      // Optional consumer chain (Lab Linkage Order Requests tab uses
      // this to call `ReceivedLabResults` with the new document id).
      // If `onSaved` throws, we stay open — same retry-friendly
      // semantics as the upload-failure path above.
      if (onSaved) await onSaved(documentId);
      toast.success(isEditMode ? 'Document updated' : 'Document added');
      onOpenChange(false);
    } catch {
      // Global mutation-cache toast surfaces the failure (per the
      // project's toast convention). The popup stays open so the
      // user can retry or correct the form.
    }
  };

  // ─── Drag / drop wiring ────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const isBusy = upload.isPending || save.isPending;

  // File drop zone — extracted into a local JSX const so we can render
  // it in different positions depending on mode. Flutter Lab Linkage
  // places it RIGHT AFTER the FILE TYPE block (above the form fields);
  // the unlocked Documents tab keeps the original layout (drop zone
  // last, after the metadata fields).
  const fileDropZone = (
    <div className="space-y-1">
      <label
        className={cn(
          'block text-xs font-medium text-text-muted',
          usesAttachLayout && 'mb-1',
        )}
      >
        File
        {!isCodeDrivenType && (
          <span className="text-status-error"> *</span>
        )}
      </label>
      {/* Single-file (Andrew 2026-06-10): the drop zone shows ONLY while
          no file is attached. Once a file is picked it collapses to the
          file row below (rename + delete) — delete brings the drop zone
          back. Matches the screenshot + the Order-Requests (locked) flow. */}
      {!hasAnyVisibleFile ? (
      // Native <label htmlFor> — the WHOLE drop zone opens the OS file
      // picker on click (browser-handled trusted gesture). No JS
      // `.click()`, so it can't be swallowed by the nested Sheet
      // focus-trap or stalled by the dev SignalR storm.
      <label
        htmlFor={fileInputId}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'rounded-md border-2 border-dashed transition-colors cursor-pointer',
          'flex flex-col items-center justify-center gap-3 py-8 px-6',
          'focus-within:ring-2 focus-within:ring-brand-primary',
          dragActive
            ? 'border-brand-primary bg-blue-100/40'
            : 'border-border-default bg-surface-card hover:bg-grey-100',
        )}
      >
        {/* Unified drop-zone illustration (Andrew 2026-06-09): the colourful
            3-doc card + "Drop File Here or Browse" + Browse button render the
            SAME on every surface — patient chart Docs / appointment Docs
            (unlocked) now match the Order-Requests (locked) design. Previously
            the unlocked path showed a plain single-icon "Drop files here"
            placeholder. Single-vs-multiple is not a per-caller choice either:
            `multiple` below is a hard `false` everywhere. */}
        <div className="flex items-end gap-2">
          <span
            className="inline-flex items-center justify-center w-10 h-12 rounded-md bg-purple-100 text-purple-700 -mr-3 -rotate-12 shadow-sm"
            aria-hidden
          >
            <FileText className="h-5 w-5" />
          </span>
          <span
            className="inline-flex items-center justify-center w-12 h-14 rounded-md bg-green-100 text-green-700 z-10 shadow-sm"
            aria-hidden
          >
            <FileSpreadsheet className="h-6 w-6" />
          </span>
          <span
            className="inline-flex items-center justify-center w-10 h-12 rounded-md bg-red-100 text-red-700 -ml-3 rotate-12 shadow-sm"
            aria-hidden
          >
            <FileText className="h-5 w-5" />
          </span>
        </div>
        <p className="text-sm font-semibold text-text-primary">
          Drop File Here or Browse
        </p>
        <p className="text-sm text-text-muted text-center max-w-xs">
          PDF, RTF, JPG, JPEG, PNG, CSV, XLSX &amp; DOCX file formats are
          supported. Max {maxUploadMb} MB.
        </p>
        {/* Visual-only pill — the surrounding <label> is the real click
            target, so this isn't a separate button (a nested
            button/label would double-fire or be invalid markup). */}
        <span
          className="mt-1 inline-flex items-center justify-center px-6 py-1.5 rounded-full bg-brand-primary text-white text-sm font-semibold transition-colors"
          aria-hidden
        >
          Browse
        </span>
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          // PROD-901: OS-level pre-filter so the user only sees allowed
          // file types in their picker. The `addFiles` validation still
          // runs as a defense-in-depth check for drag-and-drop and for
          // browsers that ignore the attribute.
          accept={DOCUMENT_ACCEPT_ATTR}
          // Single-file on every surface (Andrew 2026-06-10): one document
          // = one attached file.
          multiple={false}
          // `sr-only` (not `hidden`): the input stays focusable so
          // keyboard users can open the picker with Space/Enter; the
          // label handles pointer activation.
          className="sr-only"
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            // Reset the input so re-picking the same file fires
            // the change event again.
            e.target.value = '';
          }}
        />
      </label>
      ) : null}

      {/* PROD-899: existing files (edit mode). Each row shows a
          clickable file-name link + a remove (X) icon. Removing
          marks the entry with `isDeleted: true` so the next save
          tombstones it server-side — same model as Flutter
          `pick_document_widget.dart` documentView (lines 310-372).
          The Azure blob itself is NOT deleted (HIPAA orphan-blob
          concern stays open per the 2026-05-21 cross-platform
          ticket). */}
      {existingFiles.some((f) => !f.isDeleted) && (
        <ul className="mt-2 space-y-1">
          {existingFiles.map((file, idx) => {
            if (file.isDeleted) return null;
            const key = file.id ?? `existing-${idx}-${file.fileName ?? ''}`;
            return (
              <li
                key={key}
                className="flex items-center gap-2 rounded-md border border-border-default bg-surface-card px-2 py-1.5 text-sm"
              >
                <FileText
                  className="h-4 w-4 text-text-muted shrink-0"
                  aria-hidden
                />
                {file.url ? (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 truncate text-brand-primary hover:underline"
                  >
                    {file.fileName ?? '—'}
                  </a>
                ) : (
                  <span className="flex-1 truncate">
                    {file.fileName ?? '—'}
                  </span>
                )}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        aria-label={`Remove ${file.fileName ?? 'file'}`}
                        onClick={() =>
                          setExistingFiles((prev) =>
                            prev.map((f, i) =>
                              i === idx ? { ...f, isDeleted: true } : f,
                            ),
                          )
                        }
                        className={`${ROW_ICON_BUTTON} text-status-error`}
                      />
                    }
                  >
                    <X className="size-4" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent>Remove</TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}

      {/* Selected files list (new uploads added this session).
          PROD-903: pencil per row enters inline edit mode; check
          commits, X cancels. Renamed value is stored on the underlying
          File via `new File([blob], newName, {…})`, and the multipart
          upload's filename header ships the new value. */}
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((row) => {
            const isRenaming = renamingLocalId === row.localId;
            const ext = extensionFor(row.file.name);
            return (
              <li
                key={row.localId}
                className="flex items-center gap-2 rounded-md border border-border-default bg-surface-card px-2 py-1.5 text-sm"
              >
                <FileText
                  className="h-4 w-4 text-text-muted shrink-0"
                  aria-hidden
                />
                {isRenaming ? (
                  <>
                    <input
                      type="text"
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitRenameRow(row);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRenameRow();
                        }
                      }}
                      // PROD-903: Flutter caps the EDIT input at
                      // `100 - extension.length` so the final
                      // `name+ext` stays under 100 chars total
                      // (`pick_document_widget.dart:404`).
                      maxLength={Math.max(1, 100 - ext.length)}
                      aria-label={`Rename ${row.file.name}`}
                      className="flex-1 h-8 px-2 rounded border border-border-default bg-grey-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                    <span className="text-text-hint">{ext}</span>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-label="Confirm rename"
                            onClick={() => commitRenameRow(row)}
                            className={`${ROW_ICON_BUTTON} text-text-secondary`}
                          />
                        }
                      >
                        <Check className="size-4" aria-hidden />
                      </TooltipTrigger>
                      <TooltipContent>Confirm rename</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-label="Cancel rename"
                            onClick={cancelRenameRow}
                            className={`${ROW_ICON_BUTTON} text-text-secondary`}
                          />
                        }
                      >
                        <X className="size-4" aria-hidden />
                      </TooltipTrigger>
                      <TooltipContent>Cancel rename</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate">{row.file.name}</span>
                    <span className="text-text-hint">
                      {(row.file.size / 1024).toFixed(1)} KB
                    </span>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-label={`Rename ${row.file.name}`}
                            onClick={() => startRenameRow(row)}
                            className={`${ROW_ICON_BUTTON} text-text-secondary`}
                          />
                        }
                      >
                        <Pencil className="size-4" aria-hidden />
                      </TooltipTrigger>
                      <TooltipContent>Rename</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-label={`Remove ${row.file.name}`}
                            onClick={() => removeFile(row.localId)}
                            className={`${ROW_ICON_BUTTON} text-status-error`}
                          />
                        }
                      >
                        <X className="size-4" aria-hidden />
                      </TooltipTrigger>
                      <TooltipContent>Remove</TooltipContent>
                    </Tooltip>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          'flex flex-col gap-0 p-0',
          usesAttachLayout
            ? 'w-[640px] sm:max-w-[640px]'
            : 'w-[504px] sm:max-w-[504px]',
        )}
      >
        {/* Header — Cancel (left) / centred title / Save (right). Same on
            every surface (Andrew 2026-06-09): patient chart Docs +
            appointment Docs (unlocked) now use the same top-bar header as
            Order Requests, instead of a left title + bottom Save/Cancel.
            SheetTitle satisfies the dialog a11y contract; SheetDescription
            is screen-reader-only (no visible subtitle — matches the locked
            design). */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border-default">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
            className="text-sm text-brand-primary disabled:opacity-50"
          >
            Cancel
          </button>
          <SheetTitle className="text-base font-bold text-[var(--color-brand-depth)]">
            {isEditMode ? 'Edit File' : 'New File'}
          </SheetTitle>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={cn(
              'text-sm font-semibold transition-colors',
              canSubmit
                ? 'text-brand-primary'
                : 'text-text-muted opacity-60 cursor-not-allowed',
            )}
          >
            {isBusy ? 'Saving…' : 'Save'}
          </button>
          <SheetDescription className="sr-only">
            {isEditMode
              ? 'Update this document. Existing files stay attached unless you remove them.'
              : 'Attach a document to this patient. Required: type, name, date, and at least one file.'}
          </SheetDescription>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-6 space-y-6">
          {/* Samer PR #339 review item 1 — visible warning when the
              org's `maxDocumentUploadLimit` is missing on auth-store.
              Surfaces the bad config to the user BEFORE they commit
              PHI rather than silently falling back to a permissive
              default. The fallback is preserved (Flutter parity) but
              the misconfig is no longer silent. */}
          {uploadLimitMissing ? (
            <div
              role="alert"
              className="rounded-md border border-amber-700/40 bg-amber-100/60 px-3 py-2 text-sm text-amber-700"
            >
              {TOAST_UPLOAD_LIMIT_MISSING}
            </div>
          ) : null}

          {/* One-type FILE TYPE block — single big icon + label, centered
              above the form fields, replacing the tab row. Keyed off
              `lockedType` and NOT off `usesAttachLayout`: it answers "what is
              the one type?", so a caller with the same chrome but two types on
              offer (Technician Worklist) gets the live tab row below instead. */}
          {lockedType ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-text-muted">
                File type
              </p>
              {(() => {
                const lockedOpt = TYPE_OPTIONS_V1.find(
                  (o) => o.value === lockedType,
                );
                if (!lockedOpt) return null;
                const Icon = lockedOpt.icon;
                return (
                  <div className="flex flex-col items-start gap-1">
                    <Icon className="h-8 w-8 text-brand-primary" aria-hidden />
                    <span className="text-sm font-semibold text-brand-primary">
                      {lockedOpt.label}
                    </span>
                  </div>
                );
              })()}
            </div>
          ) : null}
          {/* The attach-a-file layout puts the file upload tool FIRST, before
              the form fields — matches the Flutter Lab upload screen ordering,
              and reads correctly on any surface whose whole job is attaching a
              file to a thing that already exists. */}
          {usesAttachLayout ? fileDropZone : null}
          {/* Type picker — horizontal icon-tabs row matching the Flutter
              widget (Note · Form · Lab · Radiology · Procedure · Admin &
              Referrals). The active tab takes brand-primary on icon +
              label; inactive tabs are muted. Labels can wrap onto a
              second line ("Admin & Referrals") without disrupting the
              row height.
              Hidden when `lockedType` is set (Lab Linkage Order
              Requests tab — user is uploading a result, not picking a
              category), and by nothing else: `attachFileLayout` is chrome and
              leaves this row alone. NARROWED when `allowedTypes` is set (Technician
              Worklist — a session report is a Radiology or a Procedure and the
              technician picks which): the row stays live, it just offers
              fewer tabs, so everything below reads `typeOptions` and never
              `TYPE_OPTIONS_V1` directly.
              In EDIT mode the row is SHOWN but LOCKED (display-only,
              current type highlighted, others dimmed, non-interactive) — matches
              Flutter, which renders the type row read-only on edit. The existing
              row's type can't change (switching mid-edit would break code-driven
              forms); the user just sees what type the document is. */}
          {lockedType ? null : (
          <div
            role="tablist"
            aria-label="File type"
            className={cn(
              'flex min-h-[64px] items-start',
              // Six tabs: distributed, exactly as before. Fewer: grouped at
              // the start, one tab-gap apart, so they read as a pair.
              isNarrowedTypeRow ? 'justify-start gap-2' : 'justify-between gap-1',
            )}
          >
            {typeOptions.map((opt, idx) => {
              const Icon = opt.icon;
              const selected = docType === opt.value;
              const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
                // WAI-ARIA tab pattern roving — arrow keys move focus +
                // selection, Home/End jump to ends. Only the active tab
                // is reachable via Tab; the rest are tabindex=-1 below
                // and surfaced via these handlers. Samer review PR #159 §2.
                let nextIdx: number | null = null;
                switch (e.key) {
                  case 'ArrowRight':
                    nextIdx = (idx + 1) % typeOptions.length;
                    break;
                  case 'ArrowLeft':
                    nextIdx =
                      (idx - 1 + typeOptions.length) %
                      typeOptions.length;
                    break;
                  case 'Home':
                    nextIdx = 0;
                    break;
                  case 'End':
                    nextIdx = typeOptions.length - 1;
                    break;
                  default:
                    return;
                }
                e.preventDefault();
                handleTypeChange(typeOptions[nextIdx].value);
                typeTabRefs.current[nextIdx]?.focus();
              };
              return (
                <button
                  key={opt.value}
                  ref={(el) => {
                    typeTabRefs.current[idx] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  // EDIT mode: the row is display-only — disable interaction so
                  // the type is visible but cannot change (PROD-1243; matches the
                  // Flutter read-only type row on edit).
                  disabled={isEditMode}
                  aria-disabled={isEditMode || undefined}
                  // Roving tabindex: only the active tab is in the Tab
                  // order; inactive tabs are reachable via arrow keys
                  // from the active one. (Disabled in edit mode → out of tab order.)
                  tabIndex={selected ? 0 : -1}
                  onClick={isEditMode ? undefined : () => handleTypeChange(opt.value)}
                  onKeyDown={isEditMode ? undefined : onKeyDown}
                  className={cn(
                    'flex w-[64px] min-h-[56px] flex-col items-center gap-1 rounded px-1 py-1.5 transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-brand-primary',
                    selected ? 'text-brand-primary' : 'text-text-muted',
                    // No hover affordance + dim the unselected tabs when locked.
                    isEditMode ? 'cursor-default' : !selected && 'hover:text-text-primary',
                    isEditMode && !selected && 'opacity-50',
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                  <span
                    className={cn(
                      'text-center text-xs leading-tight',
                      selected ? 'font-semibold' : 'font-medium',
                    )}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
          )}

          {/* File upload — directly under the type tabs (Flutter order:
              FILE TYPE → FILE → type-specific fields). Unlocked path; the
              locked path already places it under the single FILE TYPE. */}
          {usesAttachLayout ? null : fileDropZone}

          {/* Form-template picker (only when type=Form).
              PROD-1051 doc-forms (Andrew 2026-06-17): when the caller supports in-app
              fill-and-sign, the "Form template" dropdown lists the document-mode CONSENT
              templates and picking one routes to the parent's fill-and-sign popup. Callers
              that don't support it fall back to the legacy org-form-template download
              dropdown below. The consent query lives in the child so it only fires when the
              parent opted in AND the Form type is active. */}
          {docType === DocumentType.Forms && onPickConsentTemplate ? (
            <InAppConsentFormPicker
              onPick={onPickConsentTemplate}
              onUploadExternal={onUploadSignedConsent}
            />
          ) : docType === DocumentType.Forms ? (
            <div className="space-y-1">
              <label className="text-xs font-medium text-text-muted">
                Form template
              </label>
              {orgForms.isLoading ? (
                <p className="text-sm text-text-muted">Loading templates…</p>
              ) : orgForms.isError ? (
                <p className="text-sm text-status-error">
                  Couldn't load form templates.
                </p>
              ) : (orgForms.data ?? []).length === 0 ? (
                <p className="text-sm text-text-muted">
                  No form templates configured for this organization.
                </p>
              ) : (
                <Select
                  value={pickedFormId}
                  onValueChange={(v) => {
                    const id = v ?? '';
                    setPickedFormId(id);
                    // Selecting a template auto-downloads the blank form so
                    // the user can fill + re-upload it (Andrew 2026-06-10).
                    const picked = (orgForms.data ?? []).find(
                      (f) => f.id === id,
                    );
                    const url = resolveFormTemplateUrl(picked);
                    if (url) {
                      void downloadCrossOrigin(
                        url,
                        resolveFormTemplateFilename(picked),
                      );
                    }
                  }}
                >
                  {/* Full-width to match the other inputs (audit 2026-06-09). */}
                  <SelectTrigger className="w-full">
                    {/* base-ui `Select.Value` renders the raw VALUE (the
                        template id GUID), not the item label. Map id →
                        display via the function child or the trigger shows
                        the GUID (canonical fix — see void-payment-dialog
                        PROD-1006 / memory feedback_baseui_selectvalue_label). */}
                    <SelectValue placeholder="Pick a form template…">
                      {(value) => {
                        const f = (orgForms.data ?? []).find(
                          (x) => x.id === value,
                        );
                        return f?.formName ?? f?.name ?? null;
                      }}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(orgForms.data ?? []).map((f) => (
                      <SelectItem
                        key={f.id ?? f.formName ?? f.name}
                        value={(f.id as string) ?? ''}
                      >
                        {f.formName ?? f.name ?? '—'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Download link for the picked template. Cross-origin
                  download (Samer review PR #137 §3): the `download`
                  attribute is IGNORED by browsers when the href is
                  cross-origin, and Azure-blob URLs always are. The
                  button below fetches the blob → ObjectURL → triggers
                  a synthetic anchor click so the file actually
                  downloads with the right name instead of navigating
                  to the blob URL inline (which opens PDFs in the
                  browser viewer and force-renders DOCs as raw XML).
                  Fallback: if fetch fails (CORS, network, expired
                  SAS), open the URL in a new tab as a last resort. */}
              {resolveFormTemplateUrl(pickedTemplate) ? (
                <button
                  type="button"
                  onClick={() =>
                    downloadCrossOrigin(
                      resolveFormTemplateUrl(pickedTemplate) as string,
                      resolveFormTemplateFilename(pickedTemplate),
                    )
                  }
                  className="mt-1 inline-flex items-center gap-1 text-sm text-brand-primary hover:underline"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Download template
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Name — Procedure / Radiology types use a code picker
              instead of a free-text name input. The picked code's
              display string ships as both `name` and `data.title` on
              the wire (Flutter parity). */}
          {isCodeDrivenType ? (
            <div className="space-y-1">
              <label className="text-xs font-medium text-text-muted">
                {docType === DocumentType.Procedure
                  ? 'Procedure name'
                  : 'Radiology name'}{' '}
                <span className="text-status-error">*</span>
              </label>
              <CodePicker
                kind={
                  docType === DocumentType.Procedure ? 'procedure' : 'radiology'
                }
                value={pickedCode}
                onChange={setPickedCode}
              />
            </div>
          ) : docType === DocumentType.Forms ? null : (
            <div className="space-y-1">
              <label className="text-xs font-medium text-text-muted">
                {/* PROD-905: Notes type ships verbatim Flutter label
                    "Reason for Visit" + placeholder "Enter reason for
                    visit" (`note_document_widget.dart:65, :76`). All
                    other unlocked types keep "Name". Locked-mode (Lab
                    Linkage) keeps "File Name". Admin Referrals retain
                    "Name" — Flutter has TWO inputs there (File Name +
                    Reason); the full 2-input port is a separate
                    ticket. */}
                {usesAttachLayout
                  ? 'File name'
                  : docType === DocumentType.Notes
                    ? 'Reason for visit'
                    : docType === DocumentType.Labs ||
                        docType === DocumentType.AdminReferrals
                      ? 'File name'
                      : 'Name'}{' '}
                <span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                // PROD-906: Flutter caps the name field at 50 chars
                // (`note_document_widget.dart:61`). Mirror so users
                // don't learn the limit on save.
                maxLength={50}
                placeholder={
                  usesAttachLayout
                    ? 'File name'
                    : docType === DocumentType.Notes
                      ? 'Enter reason for visit'
                      : docType === DocumentType.Labs ||
                          docType === DocumentType.AdminReferrals
                        ? 'Enter file name'
                        : 'Document name'
                }
                className="w-full h-10 px-3 rounded-md border border-border-default bg-surface-card text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          )}

          {/* Document date — label changes per type to mirror Flutter
              ("procedure date" / "imaging date" / "lab test date" /
              "document date"). */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-text-muted">
              {docType === DocumentType.Procedure
                ? 'Procedure date'
                : docType === DocumentType.Radiology
                  ? 'Imaging date'
                  : docType === DocumentType.Labs
                    ? 'Lab test date'
                    : docType === DocumentType.Forms
                      ? 'Sign date'
                      : docType === DocumentType.Notes
                        ? 'Note date'
                        : 'Date'}{' '}
              <span className="text-status-error">*</span>
            </label>
            <DatePickerField
              value={documentDate}
              onChange={setDocumentDate}
              // PROD-1770(a): a document date is when the document was ISSUED, so
              // it cannot be in the future. Every one of Flutter's six live
              // sub-forms sets `lastDate: DateTime.now()` (note :95, forms :197,
              // lab :155, radiology :115, procedure :117, referral :95) and none
              // sets a firstDate — a genuinely old document must stay selectable.
              // `DatePickerField` bounds are opt-in and nobody opted in here.
              //
              // `max` takes a Date, so NOT `todayLocalYmd()` — that returns a
              // YYYY-MM-DD string and is the right helper for a native
              // <input type="date">, not for this prop.
              max={new Date()}
              className="mt-1"
            />
          </div>

          {/* Optional metadata.
              The attach-a-file layout hides Doctor name + Tags per the
              Flutter design — only Facility is surfaced. */}
          {usesAttachLayout ? (
            <div className="space-y-1">
              <label className="text-xs font-medium text-text-muted">
                Facility name
              </label>
              <input
                type="text"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                // PROD-906: Flutter `note_document_widget.dart:150` cap.
                maxLength={50}
                placeholder="Optional"
                className="w-full h-10 px-3 rounded-md border border-border-default bg-surface-card text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          ) : (
            <>
              {/* Doctor name — Flutter renders it ONLY on the Note sub-form
                  (`note_document_widget.dart:104`). Lab / Form / Procedure /
                  Radiology / (non-beta) Admin have NO doctor field. */}
              {docType === DocumentType.Notes ? (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-text-muted">
                    Doctor name
                  </label>
                  <input
                    type="text"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    // PROD-906: Flutter `note_document_widget.dart:122` cap.
                    maxLength={50}
                    placeholder="Optional"
                    className="w-full h-10 px-3 rounded-md border border-border-default bg-surface-card text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>
              ) : null}
              {/* Facility name — every type EXCEPT Form (Flutter Form has no
                  facility field; `forms_document_widget.dart` renders only
                  the form-name dropdown + sign date). */}
              {docType !== DocumentType.Forms ? (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-text-muted">
                    Facility name
                  </label>
                  <input
                    type="text"
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                    // PROD-906: Flutter `note_document_widget.dart:150` cap.
                    maxLength={50}
                    placeholder="Optional"
                    className="w-full h-10 px-3 rounded-md border border-border-default bg-surface-card text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>
              ) : null}
              {/* No Tags input — Flutter's New File popup has no tags field
                  on any sub-form (audit 2026-06-09). The `tags` value still
                  round-trips in edit mode (hydrated from the existing doc),
                  so editing a doc that already has tags doesn't wipe them. */}
            </>
          )}

          {/* Lab rows — only renders for Lab type. Each row needs a
              picked lab + result + unit to count as valid; rows still
              empty when the user clicks Save are silently dropped.
              Locked-Lab (Lab Linkage) layout matches Flutter:
                • Section heading + subtitle (no red asterisk —
                  structured rows are OPTIONAL when uploading a result)
                • Starts with zero rows; the "Add Another Lab" button
                  is the entry point
                • Top border separator above the section */}
          {docType === DocumentType.Labs ? (
            <div className="space-y-2 pt-4 border-t border-border-default">
              {/* Order-Requests (locked) lab UI on every surface
                  (Andrew 2026-06-10): heading + subtitle, zero-row start,
                  card/editor rows, "Add Another Lab". */}
              <div className="space-y-1">
                <label className="block font-medium text-text-muted text-xs">
                  Lab test results
                </label>
                <p className="text-sm text-text-muted">
                  You can add lab test results one by one.
                </p>
              </div>
              <div className="space-y-3">
                {labRows.map((entry) => {
                    // Each row toggles between a compact read-only card
                    // (lab name + unit + edit/delete) and a full editor
                    // (lab picker, result + unit, Done/Cancel). Only one
                    // row edits at a time — "Add Another Lab" hides while
                    // any row is editing.
                      if (entry.isEditing) {
                        const labCodeFilled = !!entry.row.labName?.code;
                        const allFilled =
                          labCodeFilled &&
                          !!entry.row.result?.trim() &&
                          !!entry.row.unitType?.code;
                        return (
                          <div
                            key={entry.key}
                            className="space-y-4 rounded-[var(--radius-lg)] border border-border-default p-4 bg-surface-card"
                          >
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-text-muted">
                                Lab test name{' '}
                                <span className="text-status-error">*</span>
                              </label>
                              <LabNamePicker
                                value={entry.row.labName ?? null}
                                onChange={(labName) =>
                                  setLabRows((prev) =>
                                    prev.map((r) =>
                                      r.key === entry.key
                                        ? {
                                            ...r,
                                            row: {
                                              ...r.row,
                                              labName: labName ?? undefined,
                                            },
                                          }
                                        : r,
                                    ),
                                  )
                                }
                                compact
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <label className="block text-xs font-medium text-text-muted">
                                  Result
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={entry.row.result ?? ''}
                                  onChange={(e) =>
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? {
                                              ...r,
                                              row: {
                                                ...r.row,
                                                result: e.target.value,
                                              },
                                            }
                                          : r,
                                      ),
                                    )
                                  }
                                  placeholder="Enter result"
                                  className="h-10 w-full px-3 rounded-md border border-border-default bg-grey-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs font-medium text-text-muted">
                                  Unit
                                </label>
                                <UnitPicker
                                  value={entry.row.unitType ?? null}
                                  onChange={(unitType) =>
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? {
                                              ...r,
                                              row: {
                                                ...r.row,
                                                unitType: unitType ?? undefined,
                                              },
                                            }
                                          : r,
                                      ),
                                    )
                                  }
                                  compact
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Done — Flutter `enableSave` gate:
                                  only requires the lab name to be
                                  picked (result + unit can be filled
                                  later). */}
                              <button
                                type="button"
                                disabled={!labCodeFilled}
                                onClick={() =>
                                  setLabRows((prev) =>
                                    prev.map((r) =>
                                      r.key === entry.key
                                        ? { ...r, isEditing: false }
                                        : r,
                                    ),
                                  )
                                }
                                className={cn(
                                  'h-8 px-4 rounded-md text-sm font-semibold transition-colors',
                                  labCodeFilled
                                    ? 'bg-brand-primary text-white hover:bg-[var(--color-brand-depth)]'
                                    : 'bg-grey-100 text-text-hint cursor-not-allowed',
                                )}
                              >
                                Done
                              </button>
                              {/* Cancel — VERBATIM Flutter rule
                                  (lab_document_widget.dart:514-541).
                                  The asymmetry is intentional:
                                    • Partial / empty row → cancel
                                      treats the in-progress add as
                                      "back out", drops the row. The
                                      row has no commit point yet, so
                                      throwing it away is the natural
                                      undo.
                                    • Fully-filled row → cancel is
                                      "stop editing" (the user
                                      already had a valid record;
                                      Done would have closed it too).
                                      Keeping the data avoids
                                      accidentally destroying a
                                      completed entry on a stray
                                      Cancel click.
                                  Matches Flutter byte-for-byte —
                                  changing the rule diverges from PM
                                  parity and would surface in side-
                                  by-side QA. */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (allFilled) {
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? { ...r, isEditing: false }
                                          : r,
                                      ),
                                    );
                                  } else {
                                    setLabRows((prev) =>
                                      prev.filter((r) => r.key !== entry.key),
                                    );
                                  }
                                }}
                                className="h-8 px-4 rounded-md text-sm font-semibold text-text-primary bg-grey-100 hover:bg-grey-200 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        );
                      }
                      // Read-only summary card (Flutter `labView`).
                      return (
                        <div
                          key={entry.key}
                          className="flex items-center justify-between rounded-[var(--radius-lg)] border border-border-default bg-surface-card px-4 py-3"
                        >
                          <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-base font-bold text-text-primary truncate">
                              {entry.row.labName?.display ?? '—'}
                            </span>
                            {entry.row.unitType?.display ? (
                              <span className="text-sm font-semibold text-brand-primary">
                                {entry.row.unitType.display}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-3 shrink-0 ml-3">
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    aria-label="Edit lab row"
                                    onClick={() =>
                                      setLabRows((prev) =>
                                        prev.map((r) =>
                                          r.key === entry.key
                                            ? { ...r, isEditing: true }
                                            : r,
                                        ),
                                      )
                                    }
                                    className={`${ROW_ICON_BUTTON} text-text-secondary`}
                                  />
                                }
                              >
                                <Pencil className="size-4" aria-hidden />
                              </TooltipTrigger>
                              <TooltipContent>Edit lab row</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    aria-label="Delete lab row"
                                    onClick={() =>
                                      setLabRows((prev) =>
                                        prev.filter((r) => r.key !== entry.key),
                                      )
                                    }
                                    className={`${ROW_ICON_BUTTON} text-status-error`}
                                  />
                                }
                              >
                                <Trash2 className="size-4" aria-hidden />
                              </TooltipTrigger>
                              <TooltipContent>Delete lab row</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      );
                  })}
              </div>
              {/* "Add Another Lab" — hidden while ANY row is in edit mode
                  (Flutter parity: only one row editable at a time). */}
              {!labRows.some((r) => r.isEditing) ? (
                <button
                  type="button"
                  onClick={() =>
                    setLabRows((prev) => [
                      ...prev,
                      { key: nextLabRowKey(), row: newLabRow(), isEditing: true },
                    ])
                  }
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border-default bg-surface-card py-2.5 text-base font-bold text-brand-primary hover:bg-blue-100/40 transition-colors"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add Another Lab
                </button>
              ) : null}
            </div>
          ) : null}

        </div>

      </SheetContent>
    </Sheet>
  );
}
